/*
 * Product gallery thumbnail rail — sizing and controls.
 *
 * The theme lays this rail out vertically only from 1024px up, and its Swiper
 * switches direction at the same breakpoint. Below it the rail collapses to a
 * row of tiny squares under the image. It also sizes every tile from a number
 * baked into the markup at render time, so the rail cannot follow the column
 * once the column is fluid.
 *
 * Fighting that from CSS desynchronises Swiper: it caches slide geometry at
 * init, so tiles restyled underneath it stop mapping to the right image and
 * clicking a thumbnail does nothing. Everything here therefore goes through
 * Swiper's own API — change the direction, write the sizes, then update() so
 * it recalculates against what is actually on the page.
 *
 * Sizing rule: the rail is exactly as tall as the square main image beside it.
 *
 *   tile = (row - gapX - (visible - 1) * gapY) / (visible + 1)
 *
 * which falls out of rail height == image height, image being square and the
 * row being the tile plus the gap plus the image.
 */
(function () {
  const ROOT = '.m-product-media--slider__thumbnails'
  const VERTICAL_FROM = 768
  const GAP_X = 8
  const GAP_Y = 5
  const VISIBLE = 4
  const MAX_TILE = 104

  const swiperOf = (el) => (el && el.swiper) || null

  /* Tile size is fluid but capped: it grows with the row up to a ceiling, so a
     wide desktop does not end up with thumbnails the size of a small product
     image. The rail is always exactly as tall as the square image beside it -
     the tile maths only decides how many fit inside that height. */
  function measure(slider) {
    const row = slider.clientWidth
    if (!row) return null
    const fluid = Math.floor((row - GAP_X - (VISIBLE - 1) * GAP_Y) / (VISIBLE + 1))
    const max = parseInt(slider.dataset.thumbMax, 10)
    const tile = Math.min(fluid, Number.isFinite(max) && max > 0 ? max : MAX_TILE)
    // Taking the rail's height straight off the image rather than re-adding the
    // tiles keeps the two exactly level whatever the tile works out to, so a
    // capped tile just means one more fits rather than the rail going short.
    return { tile, rail: row - tile - GAP_X }
  }

  function apply(root) {
    const slider = root.closest('.m-product-media--slider')
    if (!slider) return
    const nav = swiperOf(root.querySelector('.nav-swiper-container'))
    const main = swiperOf(slider.querySelector('.swiper-container:not(.nav-swiper-container)'))
    const vertical = window.innerWidth >= VERTICAL_FROM

    if (!vertical) {
      slider.style.removeProperty('--m-thumb-w')
      slider.style.removeProperty('--m-thumb-rail')
      slider.removeAttribute('data-thumb-fit')
      root.querySelectorAll('.swiper-slide').forEach((s) => s.style.removeProperty('--slide-height'))
      const strip = root.querySelector('.swiper-wrapper')
      if (strip) strip.style.removeProperty('min-width')
      if (nav && nav.params.direction !== 'horizontal') nav.changeDirection('horizontal')
      if (nav) nav.update()
      if (main) main.update()
      return
    }

    const size = measure(slider)
    if (!size) return

    slider.style.setProperty('--m-thumb-w', size.tile + 'px')
    slider.style.setProperty('--m-thumb-rail', size.rail + 'px')
    slider.setAttribute('data-thumb-fit', 'vertical')

    // The markup carries a per-slide height and a wrapper min-width from render
    // time, both fixed numbers. Rewrite them so the tiles come out square at
    // the measured size and Swiper measures what it is actually showing.
    const wrapper = root.querySelector('.swiper-wrapper')
    if (wrapper) wrapper.style.minWidth = size.tile + 'px'
    root.querySelectorAll('.swiper-slide').forEach((s) => {
      s.style.setProperty('--slide-height', size.tile + 'px')
    })

    if (nav) {
      if (nav.params.direction !== 'vertical') nav.changeDirection('vertical')
      nav.update()
    }
    if (main) main.update()
  }

  function bind(root) {
    if (root.dataset.thumbNavBound) return
    const nav = root.querySelector('.nav-swiper-container')
    if (!swiperOf(nav)) return
    root.dataset.thumbNavBound = 'true'

    apply(root)

    let frame
    window.addEventListener('resize', () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => apply(root))
    })

    const swiper = swiperOf(nav)
    const prev = root.querySelector('.m-thumb-nav--prev')
    const next = root.querySelector('.m-thumb-nav--next')
    const paint = () => {
      if (prev) prev.disabled = swiper.isBeginning
      if (next) next.disabled = swiper.isEnd
    }
    if (prev) prev.addEventListener('click', () => swiper.slidePrev())
    if (next) next.addEventListener('click', () => swiper.slideNext())
    ;['slideChange', 'reachBeginning', 'reachEnd', 'fromEdge', 'resize'].forEach((e) =>
      swiper.on(e, paint)
    )
    paint()
  }

  /* The gallery's Swiper is built after the theme has fetched the product JSON,
     which can land well after DOMContentLoaded and varies with the network. A
     short retry window silently gave up on slower loads and left the rail at
     its render-time size, so keep looking until it appears. */
  function watch() {
    let waited = 0
    const tick = () => {
      const roots = [...document.querySelectorAll(ROOT)]
      roots.forEach(bind)
      const pending = roots.some((r) => !r.dataset.thumbNavBound)
      waited += 200
      if (pending && waited < 20000) setTimeout(tick, 200)
    }
    tick()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch)
  } else {
    watch()
  }
})()
